import type { ChartLikeProtocol, RadarChartOptionProtocol } from '@lighthouse/core'

import type { ECOption } from '../../components'
import { defaultColors, legendOffset, legendWidth } from './common'

const radarLegendOffset = 8

const getLegend = (params: RadarChartOptionProtocol, chartData: ChartLikeProtocol): ECOption['legend'] => {
    const { showLegend, legendTranspose, colors } = params
    const { legend } = chartData
    const palette = colors?.length > 0 ? colors : defaultColors
    const legendConfigurator: ECOption['legend'] = {
        type: 'scroll',
        show: showLegend,
        data: legend.map((name, i) => ({
            name,
            icon: 'circle',
            itemStyle: { color: palette[i % palette.length] }
        }))
    }
    switch (legendTranspose) {
        case 'top': {
            legendConfigurator.top = radarLegendOffset
            legendConfigurator.left = 'center'
            break
        }
        case 'bottom': {
            legendConfigurator.bottom = radarLegendOffset
            legendConfigurator.left = 'center'
            break
        }
        case 'left': {
            legendConfigurator.left = radarLegendOffset
            legendConfigurator.top = 'center'
            legendConfigurator.width = legendWidth
            legendConfigurator.orient = 'vertical'
            break
        }
        case 'right': {
            legendConfigurator.right = radarLegendOffset
            legendConfigurator.width = legendWidth
            legendConfigurator.top = 'center'
            legendConfigurator.orient = 'vertical'
            break
        }
        default: {
            legendConfigurator.bottom = radarLegendOffset
            legendConfigurator.left = 'center'
            break
        }
    }
    return legendConfigurator
}

type RadarBoundary = {
    top?: number
    bottom?: number
    left?: number
    right?: number
}

const getRadarBoundary = (params: RadarChartOptionProtocol): RadarBoundary => {
    const { showLegend, legendTranspose } = params
    if (!showLegend) {
        return {}
    }
    // radarLegendOffset(8) + 图例高度约25px + 间距10px ≈ 43px; 左右: 8 + legendWidth(150) + 10 = 168
    switch (legendTranspose) {
        case 'top':
            return { top: 43 }
        case 'bottom':
            return { bottom: 43 }
        case 'left':
            return { left: radarLegendOffset + legendWidth + 10 }
        case 'right':
            return { right: radarLegendOffset + legendWidth + 10 }
        default:
            return { bottom: 43 }
    }
}

const getIndicators = (chartData: ChartLikeProtocol) => {
    const { xAxis, series } = chartData
    const globalMax = Math.max(...series.flatMap(s => s.value.map(Number)), 0)
    const sharedMax = globalMax * 1.2 || 1
    return xAxis.map(xAxisItem => ({
        name: xAxisItem[0] ?? '',
        max: sharedMax
    }))
}

const getSeries = (params: RadarChartOptionProtocol, chartData: ChartLikeProtocol): ECOption['series'] => {
    const { colors, showLabel } = params
    const palette = colors?.length > 0 ? colors : defaultColors
    const { series, legend } = chartData

    return [
        {
            type: 'radar',
            label: { show: showLabel, fontSize: 12 },
            data:
                series?.length > 0
                    ? series.map((s, i) => {
                          const color = palette[i % palette.length]
                          return {
                              name: legend?.[i] ?? s.name,
                              value: s.value.map(Number),
                              areaStyle: { color, opacity: 0.25 },
                              lineStyle: { color, width: 2 },
                              itemStyle: { color }
                          }
                      })
                    : []
        }
    ]
}

const getRadarOption = (params: RadarChartOptionProtocol, chartData?: ChartLikeProtocol): ECOption => {
    const { colors, showHeadTitle, title, radarShape = 'polygon' } = params
    const palette = colors?.length > 0 ? colors : defaultColors

    if (!chartData || chartData.xAxis.length === 0) {
        return {
            color: palette,
            title: {
                text: showHeadTitle && title ? title : undefined,
                textStyle: {
                    fontSize: 24,
                    fontWeight: 500,
                    color: '#101828'
                }
            }
        }
    }

    return {
        color: palette,
        title: {
            text: showHeadTitle && title ? title : undefined,
            textStyle: {
                fontSize: 24,
                fontWeight: 500,
                color: '#101828'
            }
        },
        tooltip: { show: true },
        legend: getLegend(params, chartData),
        radar: {
            ...getRadarBoundary(params),
            shape: radarShape,
            splitNumber: 4,
            splitLine: {
                lineStyle: { color: '#E4E7EC', width: 1 }
            },
            splitArea: { show: false },
            axisLine: {
                lineStyle: { color: '#E4E7EC' }
            },
            axisName: {
                color: '#667085',
                fontSize: 12
            },
            indicator: getIndicators(chartData)
        },
        series: getSeries(params, chartData)
    }
}

export default getRadarOption
